/**
 * Faction data and configuration
 */

import type { FactionId, FactionData, UnitStats, BuildingStats } from '@/types';

export const FACTIONS: Record<FactionId, FactionData> = {
  yan: {
    id: 'yan',
    name: '炎国',
    color: 0xc73e3a,
    secondaryColor: 0xd4a843,
    attackBonus: 1.15,
    defenseBonus: 1.0,
    speedBonus: 1.0,
    incomeBonus: 1.0,
  },
  han: {
    id: 'han',
    name: '寒国',
    color: 0x5b9bd5,
    secondaryColor: 0xe8e8e8,
    attackBonus: 1.0,
    defenseBonus: 1.2,
    speedBonus: 1.0,
    incomeBonus: 1.0,
  },
  lin: {
    id: 'lin',
    name: '林国',
    color: 0x4caf50,
    secondaryColor: 0x795548,
    attackBonus: 1.0,
    defenseBonus: 1.0,
    speedBonus: 1.0,
    incomeBonus: 1.0,
  },
  shang: {
    id: 'shang',
    name: '商国',
    color: 0xffd700,
    secondaryColor: 0x7b1fa2,
    attackBonus: 1.0,
    defenseBonus: 1.0,
    speedBonus: 1.0,
    incomeBonus: 1.4,
  },
  xuan: {
    id: 'xuan',
    name: '玄国',
    color: 0x4a148c,
    secondaryColor: 0x212121,
    attackBonus: 1.0,
    defenseBonus: 1.0,
    speedBonus: 1.0,
    incomeBonus: 1.0,
  },
  tie: {
    id: 'tie',
    name: '铁国',
    color: 0x607d8b,
    secondaryColor: 0xff5722,
    attackBonus: 1.0,
    defenseBonus: 1.0,
    speedBonus: 0.9,
    incomeBonus: 1.0,
  },
  yun: {
    id: 'yun',
    name: '云国',
    color: 0x03a9f4,
    secondaryColor: 0xeceff1,
    attackBonus: 1.0,
    defenseBonus: 1.0,
    speedBonus: 1.35,
    incomeBonus: 1.0,
  },
  neutral: {
    id: 'neutral',
    name: '中立',
    color: 0x888888,
    secondaryColor: 0xaaaaaa,
    attackBonus: 1.0,
    defenseBonus: 1.0,
    speedBonus: 1.0,
    incomeBonus: 1.0,
  },
};

export const UNIT_STATS: Record<string, UnitStats> = {
  sword: {
    type: 'sword',
    health: 100,
    attack: 15,
    defense: 5,
    attackSpeed: 1.0,
    moveSpeed: 1.0,
    range: 1.5,
    cost: 50,
    trainTime: 5,
  },
  archer: {
    type: 'archer',
    health: 60,
    attack: 20,
    defense: 2,
    attackSpeed: 1.5,
    moveSpeed: 0.9,
    range: 5.0,
    cost: 80,
    trainTime: 7,
  },
  spear: {
    type: 'spear',
    health: 120,
    attack: 25,
    defense: 8,
    attackSpeed: 1.8,
    moveSpeed: 0.8,
    range: 2.0,
    cost: 100,
    trainTime: 8,
  },
  cavalry: {
    type: 'cavalry',
    health: 150,
    attack: 30,
    defense: 6,
    attackSpeed: 1.2,
    moveSpeed: 2.0,
    range: 1.5,
    cost: 200,
    trainTime: 12,
  },
  shield: {
    type: 'shield',
    health: 200,
    attack: 8,
    defense: 15,
    attackSpeed: 2.0,
    moveSpeed: 0.6,
    range: 1.5,
    cost: 120,
    trainTime: 10,
  },
  taoist: {
    type: 'taoist',
    health: 50,
    attack: 40,
    defense: 1,
    attackSpeed: 2.5,
    moveSpeed: 0.7,
    range: 4.0,
    cost: 300,
    trainTime: 15,
  },
};

export const BUILDING_STATS: Record<string, BuildingStats> = {
  capital: {
    type: 'capital',
    health: 5000,
    cost: 0,
    buildTime: 0,
    size: { width: 4, depth: 4 },  // Visual: 3.8 x 3.8
    income: 17, // Per 5s (~50 per 15s)
  },
  barracks: {
    type: 'barracks',
    health: 500,
    cost: 200,
    buildTime: 10,
    size: { width: 3, depth: 3 },  // Visual: 2.6 x 2.4
    spawnType: 'sword',
    spawnInterval: 30,
  },
  tower: {
    type: 'tower',
    health: 800,
    cost: 300,
    buildTime: 15,
    size: { width: 3, depth: 3 },  // Visual: 2.8 x 2.8 (increased from 2x2)
  },
  mine: {
    type: 'mine',
    health: 300,
    cost: 150,
    buildTime: 8,
    size: { width: 3, depth: 3 },  // Visual: ~3.2 diameter (increased from 2x2)
    income: 8, // Per 5s (~25 per 15s)
  },
  farm: {
    type: 'farm',
    health: 200,
    cost: 100,
    buildTime: 6,
    size: { width: 3, depth: 3 },  // Visual: 2.8 x 2.4 (adjusted for crops area)
    income: 5, // Per 5s (~15 per 15s)
  },
  market: {
    type: 'market',
    health: 400,
    cost: 250,
    buildTime: 12,
    size: { width: 3, depth: 3 },  // Visual: 2.8 x 2.6
  },
  stable: {
    type: 'stable',
    health: 600,
    cost: 400,
    buildTime: 20,
    size: { width: 3, depth: 3 },  // Visual: 2.8 x 2.4
    spawnType: 'cavalry',
    spawnInterval: 60,
  },
  smithy: {
    type: 'smithy',
    health: 500,
    cost: 350,
    buildTime: 15,
    size: { width: 3, depth: 3 },  // Visual: 2.8 x 2.6 (increased from 2x2)
    spawnType: 'shield',
    spawnInterval: 45,
  },
  temple: {
    type: 'temple',
    health: 600,
    cost: 600,
    buildTime: 25,
    size: { width: 3, depth: 3 },  // Visual: 2.6 x 2.6 (centered)
    spawnType: 'taoist',
    spawnInterval: 60,
  },
  archery: {
    type: 'archery',
    health: 450,
    cost: 250,
    buildTime: 12,
    size: { width: 3, depth: 3 },  // Visual: 2.6 x 2.4 (centered)
    spawnType: 'archer',
    spawnInterval: 35,
  },
  spearcamp: {
    type: 'spearcamp',
    health: 550,
    cost: 300,
    buildTime: 14,
    size: { width: 3, depth: 3 },  // Visual: 2.6 x 2.4 (adjusted from 2x3)
    spawnType: 'spear',
    spawnInterval: 40,
  },
  wall: {
    type: 'wall',
    health: 400,
    cost: 50,
    buildTime: 3,
    size: { width: 1, depth: 1 },
  },
};

/**
 * Get counter bonus for unit type matchups
 */
export function getCounterBonus(attackerType: string, defenderType: string): number {
  // Sword beats Archer
  if (attackerType === 'sword' && defenderType === 'archer') return 1.5;
  if (attackerType === 'archer' && defenderType === 'sword') return 0.67;

  // Archer beats Spear
  if (attackerType === 'archer' && defenderType === 'spear') return 1.5;
  if (attackerType === 'spear' && defenderType === 'archer') return 0.67;

  // Spear beats Sword
  if (attackerType === 'spear' && defenderType === 'sword') return 1.5;
  if (attackerType === 'sword' && defenderType === 'spear') return 0.67;

  return 1.0;
}
